import React from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Map as MapIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  DataUsage as DataUsageIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useTranslation } from '../utils/translations';

const Home = ({ language, onTabChange }) => {
  const t = useTranslation(language);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleFeatureClick = (tabValue) => {
    onTabChange(tabValue);
    // Scroll to top of the page when tab opens
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const features = [
    {
      id: 1,
      title: t('electionIntelligenceTitle'),
      description: t('electionIntelligenceDescription'),
      icon: <AnalyticsIcon sx={{ fontSize: 48, color: '#D03A22' }} />,
      tabValue: 1,
      gradient: 'linear-gradient(135deg, #D03A22 0%, #E85A42 100%)',
      stats: t('stateLevelAnalysis')
    },
    {
      id: 2,
      title: t('surveyResponseHeatmap'),
      description: t('surveyHeatmapDescription'),
      icon: <MapIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
      tabValue: 2,
      gradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
      stats: t('geographicDistributionText')
    },
    {
      id: 3,
      title: t('rawDataVisualization'),
      description: t('rawDataDescription'),
      icon: <BarChartIcon sx={{ fontSize: 48, color: '#388e3c' }} />,
      tabValue: 3,
      gradient: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
      stats: t('comprehensiveCharts')
    }
  ];

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        minHeight: 'calc(100vh - 120px)', // Account for header and footer
        display: 'flex',
        flexDirection: 'column',
        py: 4,
        px: 3
      }}
    >
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <img
            src={isDarkMode ? "/logos/logo_dark_mode.png" : "/logos/logo_light.png"}
            alt="Precision Politics"
            style={{
              height: '250px',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>

      {/* About Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3,
          backgroundColor: isDarkMode ? '#373737' : '#ffffff',
          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TrendingUpIcon sx={{ mr: 2, color: '#D03A22', fontSize: 32 }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              color: isDarkMode ? '#e2e8f0' : '#2d3748'
            }}
          >
            {t('aboutThisPlatform')}
          </Typography>
        </Box>
        
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: isDarkMode ? '#cbd5e0' : '#4a5568',
            fontFamily: '"Poppins", sans-serif',
            mb: 2
          }}
        >
          {t('homeAboutParagraph1')}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: isDarkMode ? '#cbd5e0' : '#4a5568',
            fontFamily: '"Poppins", sans-serif',
            mb: 2
          }}
        >
          {t('homeAboutParagraph2')}
        </Typography>
      </Paper>

      {/* Feature Cards */}
      <Grid container spacing={3} sx={{ flex: 1, alignItems: 'stretch' }}>
        {features.map((feature) => (
          <Grid item xs={12} md={4} key={feature.id} sx={{ display: 'flex' }}>
            <Card 
              elevation={6}
              sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: isDarkMode ? '#373737' : '#ffffff',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                  '& .feature-icon': {
                    transform: 'scale(1.05)',
                  },
                  '& .feature-arrow': {
                    transform: 'translateX(4px)',
                  }
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleFeatureClick(feature.tabValue)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
              >
                {/* Feature Header */}
                <Box 
                  sx={{ 
                    p: 3,
                    background: feature.gradient,
                    color: 'white',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                                         className="feature-icon"
                     sx={{ 
                       mb: 2,
                       transition: 'transform 0.15s ease',
                       filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                     }}
                  >
                    {React.cloneElement(feature.icon, { sx: { fontSize: 48, color: 'white' } })}
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      opacity: 0.9,
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.85rem'
                    }}
                  >
                    {feature.stats}
                  </Typography>
                </Box>

                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      flex: 1,
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                      color: isDarkMode ? '#cbd5e0' : '#4a5568',
                      fontFamily: '"Poppins", sans-serif',
                      mb: 2
                    }}
                  >
                    {feature.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                    <Typography 
                      variant="button" 
                      sx={{ 
                        color: '#D03A22',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                                                                    {t('exploreText')}
                      </Typography>
                      <ArrowForwardIcon 
                       className="feature-arrow"
                       sx={{ 
                         color: '#D03A22',
                         transition: 'transform 0.15s ease',
                         fontSize: 20
                       }} 
                     />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Home; 